// Librairies
import { ChangeDetectionStrategy, Component, computed, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// Services
import { ChartService } from 'src/app/services/api/charts/chart.service';

// Components
import { FieldLabelComponent } from 'src/app/components/project-pages/dashboard/field-label/field-label.component';
import { fieldInfo } from 'src/app/pipes/field-info.pipe';
import { ButtonComponent, SpinnerComponent } from 'ui-library';

// Interfaces
export interface PickerField {
  path: string;           // e.g. payload.volume
  type: string;           // String, Number, Date, ObjectId…
  text?: string;          // label, hint and path, normalized for the search
}

export interface PickerGroup {
  key: string;
  label: string;          // i18n key, or a measure type as it is (Coulage)
  fields: PickerField[];
  metricNames?: string[]; // fields of one measure type (its names in the data): picking one keeps that type only
  count?: number;         // its measures, when the API counts them
}

export interface FieldPick {
  collection: string;
  path: string;
  type: string;
  metricNames?: string[];
}

// The data sources of a widget, as the client names them; `technical`: their former name, for those used to it.
export const FIELD_SOURCES: readonly { collection: string; icon: string; technical: string }[] = [
  { collection: 'metric', icon: 'insights', technical: 'Metrics' },
  { collection: 'event', icon: 'notifications', technical: 'Events' },
  { collection: 'track', icon: 'my_location', technical: 'Tracks' },
  { collection: 'attachment', icon: 'photo_camera', technical: 'Attachments' },
  { collection: 'action', icon: 'task_alt', technical: 'Actions' },
  { collection: 'task', icon: 'event_note', technical: 'Tasks' },
  { collection: 'fragment', icon: 'view_in_ar', technical: 'Fragments' },
  { collection: 'tracker', icon: 'sensors', technical: 'Trackers' },
];

const NUMBER_PARTS = ["min", "max", "speed", "ave", "mean", "avg", "weight", "volume", "battery", "accuracy", "temperature", "dist", "duration", "count", "length", "level", "iinst", "ratio", "gust"];
const NUMBER_KEYS = new Set(["lat", "lon", "latitude", "longitude", "altitude", "x", "y", "z", "base", "papp"]);

// The type of a key of a free object (payload), from its name: the API only knows the root types.
export function guessFieldType(key: string): string {
  const lower = key.toLowerCase();

  if (lower.includes('date'))
    return 'Date';

  if (NUMBER_PARTS.some(part => lower.includes(part)) || NUMBER_KEYS.has(lower))
    return 'Number';

  return 'String';
}

// The same measure type written under two names by two producers: one group, filtering on both.
export const METRIC_NAME_ALIASES: Readonly<Record<string, string>> = { 'Séquence': 'Séquences', 'Attente': 'Attentes' };

// Below this many measures, a type is left out of the menu (tests, leftovers); its fields stay under "all types".
export const MIN_MEASURES = 5;

const byPath = (a: PickerField, b: PickerField): number => a.path.localeCompare(b.path);

// The groups of a source from POST /charts/fields: its own fields, then each free object (payload, message, settings);
// the measure details per measure type when the API gives them (byName), then all of them.
export function fieldGroups(response: Record<string, any>, collection: string): PickerGroup[] {
  const general: PickerField[] = [];
  const groups: PickerGroup[] = [];

  for (const [key, value] of Object.entries(response ?? {})) {
    if (key === 'names') continue;

    if (!Array.isArray(value)) {
      if (typeof value === 'string')
        general.push({ path: key, type: value });

      continue;
    }

    const fields = (value as string[]).map(child => ({ path: `${key}.${child}`, type: guessFieldType(child) })).sort(byPath);
    if (collection === 'metric' && key === 'payload' && response['names']) {
      groups.push(...measureGroups(response['names'], response['counts']));
      const counts: Record<string, number> | undefined = response['counts'];
      groups.push({ key, label: 'editor.picker.all_types', fields, ...(counts && { count: Object.values(counts).reduce((sum, count) => sum + count, 0) }) });
    } else {
      groups.push({ key, label: '', fields });
    }
  }

  return [{ key: 'general', label: 'editor.picker.general', fields: [...general].sort(byPath) }, ...groups].filter(group => group.fields.length);
}

// One group per measure type: its aliases merged, the marginal ones left out when the API counts them.
function measureGroups(names: Record<string, string[]>, counts?: Record<string, number>): PickerGroup[] {
  const types = new Map<string, { names: string[]; keys: Set<string>; count: number }>();

  for (const [name, keys] of Object.entries(names)) {
    const canonical = METRIC_NAME_ALIASES[name] ?? name;
    const type = types.get(canonical) ?? { names: [], keys: new Set<string>(), count: 0 };
    type.names.push(name);
    keys.forEach(child => type.keys.add(child));
    type.count += counts?.[name] ?? 0;
    types.set(canonical, type);
  }

  return [...types.entries()]
    .filter(([, type]) => !counts || type.count >= MIN_MEASURES)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([canonical, type]) => ({
      key: `name:${canonical}`, label: canonical, metricNames: [...type.names].sort((a, b) => a.localeCompare(b)),
      fields: [...type.keys].map(child => ({ path: `payload.${child}`, type: guessFieldType(child) })).sort(byPath),
      ...(counts && { count: type.count })
    }));
}

const normalize = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Field picker: the sources, their groups, a search over the labels; one source per widget (an aggregation reads one collection).
@Component({
  selector: 'app-field-picker',
  standalone: true,
  imports: [FormsModule, MatFormFieldModule, MatIconModule, MatInputModule, MatTooltipModule, TranslateModule, ButtonComponent, SpinnerComponent, FieldLabelComponent],
  templateUrl: './field-picker.component.html',
  styleUrl: './field-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class FieldPickerComponent implements OnInit {
  // Services
  private readonly chartService: ChartService = inject(ChartService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  // Variables
  @Input() locked: string | null = null;                       // the source of the fields already chosen
  @Input() tenantId: string | undefined;                        // synthesis: the fields of the tenant
  @Input() cache: Record<string, PickerGroup[]> = {};           // kept by the editor: loaded once per opening of the editor
  @Output() picked: EventEmitter<FieldPick> = new EventEmitter<FieldPick>();
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();

  readonly sources = FIELD_SOURCES;

  // Signals
  readonly query = signal<string>('');
  readonly groups = signal<Record<string, PickerGroup[]>>({});
  readonly loading = signal<Record<string, boolean>>({});
  readonly openSources = signal<Set<string>>(new Set());
  readonly openGroups = signal<Set<string>>(new Set());
  private readonly search = computed(() => normalize(this.query().trim()));

  // Every source folded; the fields of the likely one (the widget source, else the measures) loaded at once.
  ngOnInit(): void {
    this.groups.set({ ...this.cache });
    this.load(this.locked ?? 'metric');
  }

  isDisabled(collection: string): boolean {
    return !!this.locked && this.locked !== collection;
  }

  isOpen(collection: string): boolean {
    return this.openSources().has(collection) || (!!this.search() && !this.isDisabled(collection));
  }

  isGroupOpen(collection: string, group: PickerGroup): boolean {
    return !!this.search() || this.openGroups().has(`${collection}/${group.key}`) || group.key === 'general';
  }

  toggleSource(collection: string): void {
    if (this.isDisabled(collection)) return;

    this.openSources.update(open => {
      const next = new Set(open);
      next.has(collection) ? next.delete(collection) : next.add(collection);
      return next;
    });

    this.load(collection);
  }

  toggleGroup(collection: string, group: PickerGroup): void {
    const key = `${collection}/${group.key}`;

    this.openGroups.update(open => {
      const next = new Set(open);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  // A search looks in every source it may pick from: they are loaded on the first letter.
  onSearch(text: string): void {
    this.query.set(text);

    if (text.trim())
      this.sources.filter(source => !this.isDisabled(source.collection)).forEach(source => this.load(source.collection));
  }

  // The groups of a source as shown: with a search, only the matching fields, the empty groups left out.
  visibleGroups(collection: string): PickerGroup[] {
    const groups = this.groups()[collection] ?? [];
    const search = this.search();

    if (!search)
      return groups;

    return groups.map(group => ({ ...group, fields: group.fields.filter(field => field.text?.includes(search)) })).filter(group => group.fields.length);
  }

  // A measure type from the glossary (fields.measures), else as written in the data.
  groupLabel(collection: string, group: PickerGroup): string {
    if (group.metricNames)
      return this.measure(group.label)?.label ?? group.label;

    return group.label ? this.translate.instant(group.label) : fieldInfo(this.translate, collection, group.key).label;
  }

  groupHint(group: PickerGroup): string {
    return group.metricNames ? this.measure(group.label)?.hint ?? '' : '';
  }

  // 85557 → 85 557 (the separator of the language).
  formatCount(count: number): string {
    return new Intl.NumberFormat(this.translate.currentLang || 'fr').format(count);
  }

  private measure(name: string): { label: string; hint: string } | null {
    const entry = this.translate.instant(`fields.measures.${name}`);
    return typeof entry?.label === 'string' ? entry : null;
  }

  pick(collection: string, group: PickerGroup, field: PickerField): void {
    this.picked.emit({ collection, path: field.path, type: field.type, ...(group.metricNames && { metricNames: group.metricNames }) });
  }

  private load(collection: string): void {
    if (this.groups()[collection] || this.loading()[collection]) return;

    this.loading.update(loading => ({ ...loading, [collection]: true }));

    const query: Record<string, string | number | boolean> = {
      collection,
      limit: collection === 'metric' || collection === 'attachment' ? 200000 : 10000,
      ...(collection === 'metric' && { byName: true }),
      ...(this.tenantId && { tenantId: this.tenantId })
    };

    this.chartService.getFields(query).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        const groups = fieldGroups(response, collection).map(group => ({
          ...group,
          fields: group.fields.map(field => {
            const info = fieldInfo(this.translate, collection, field.path);
            return { ...field, text: normalize(`${info.label} ${info.hint} ${field.path}`) };
          })
        }));

        this.cache[collection] = groups;
        this.groups.update(all => ({ ...all, [collection]: groups }));
        this.loading.update(loading => ({ ...loading, [collection]: false }));
      },
      error: () => this.loading.update(loading => ({ ...loading, [collection]: false }))
    });
  }
}
